import { css, Global } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import type { QueryClient } from '@tanstack/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import mixpanel from 'mixpanel-browser';
import { SnackbarProvider } from 'notistack';
import type { FunctionComponent } from 'react';
import { StrictMode, useContext, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { createBrowserRouter, Navigate, Route, RouterProvider, Routes } from 'react-router';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '~/shared/context/context';
import type { UserSettings } from '~/shared/data-contract/user-settings-schema';
import type { AppEnvironment } from '~/shared/environment/environment';
import { PageWithTitle } from '~/shared/page-with-title/PageWithTitle';
import { Spinner } from '~/ui/core/spinner/Spinner';
import { theme } from '~/ui/theme/cobrainer-theme';

import { AccessDeniedPage } from '../AccessDeniedPage';
import { TerminologyInitializer } from '../company-settings/tabs/organization-tab/terminology/TerminologyInitializer';
import { debugRoute } from '../debug/route-config';
import { ApplicationFetchersProvider } from '../fetchers/ApplicationFetchersContext';
import { RedirectToJobArchitectureV2 } from '../job-architecture-v2/RedirectToJobArchitectureV2';
import { LandingPage } from '../LandingPage';
import { createAlignmentRecordPageLoader } from '../legacy/alignment-record/store/alignment-record-page-loader';
import { CompanyOnboardingSetupPage } from '../legacy/company-onboarding/CompanyOnboardingSetupPage';
import { companyOnboardingRoutePath } from '../legacy/company-onboarding/route-config';
import { SkillFeedbackModalRoot } from '../legacy/feedback/SkillFeedbackModalRoot';
import { Home } from '../legacy/home/Home';
import { createMyAlignmentPageLoader } from '../legacy/my-alignment/my-alignment-page-loader';
import { createPositionsPageLoader } from '../legacy/position/position-page-loader';
import { createFetchAlignmentRecord } from '../legacy/position-management/fetch-alignment-record';
import { createPositionManagementPageLoader } from '../legacy/position-management/position-management-page-loader';
import { Welcome } from '../legacy/welcome/Welcome';
import { TabProvider } from '../mobile/TabProvider';
import { MixpanelTrackingProvider } from '../tracking/mixpanel/MixpanelTrackingProvider';
import { UrlTileQueryAdapter } from '../url-tiles/UrlTileQueryAdapter';
import type { AppDependencies } from './AppWithContext';
import { AppWithContext } from './AppWithContext';
import { renderErrorCodePage } from './error-handling/ErrorBoundaries';
import { Error404 } from './error-handling/ErrorPages';
import { snackbarComponents } from './error-handling/SnackbarComponents';
import type { LayoutVariant } from './Layout';
import { Layout } from './Layout';
import { lazyRoutesConfig } from './lazy-routes-config';
import { PageWithFeatureFlagEnabled } from './PageWithFeatureFlagEnabled';
import { PageWithPageViewTracking } from './PageWithPageViewTracking';
import { route as dashboardPageRoute } from './pages/dashboard/constants';
import { route as globalSearchRoute } from './pages/global-search-page/constants';
import { GlobalSearchPage } from './pages/global-search-page/GlobalSearch';
import { route as profilePageRoute, userProfileUrl } from './pages/profile/constants';
import { route as talentDirectoryPageRoute } from './pages/talent-directory/constants';
import { TalentDirectoryPage } from './pages/talent-directory/TalentDirectory';
import { createRoutesFromRouteDefinitions } from './routes-from-route-definitions';
import { CustomThemeProvider } from './theming';

type AppBaseProperties = {
    readonly hasNavigationMenu?: boolean;
    readonly isDataRoute: boolean;
    readonly layoutVariant?: LayoutVariant;
    readonly shouldShowFeedbackAction?: boolean;
};

const shouldUserCompleteOnboarding = (userSettings: Maybe<UserSettings>): boolean => {
    const startPage = userSettings.get('startPage');

    return startPage.equals(Maybe.just('company-onboarding')) || startPage.equals(Maybe.just('user-onboarding'));
};

const AppBase: FunctionComponent<AppBaseProperties> = (properties) => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const { featureFlagService, myUserProfile, userSettings } = applicationState;

    if (myUserProfile.isNothing) {
        return null;
    }

    if (properties.isDataRoute) {
        return (
            <Layout
                hasNavigationMenu={properties.hasNavigationMenu}
                shouldShowFeedbackAction={properties.shouldShowFeedbackAction}
                variant={properties.layoutVariant}
            />
        );
    }

    const isTrial = applicationState.customization.get('tenantMode').equals(Maybe.just('trial'));

    const initialPageForTrialTenant = shouldUserCompleteOnboarding(userSettings) ? (
        <CompanyOnboardingSetupPage />
    ) : (
        <Home />
    );

    return (
        <Routes>
            <Route element={<Layout />}>
                {createRoutesFromRouteDefinitions(
                    { translate },
                    { featureFlagService, routeDefinitions: lazyRoutesConfig },
                )}
                <Route
                    element={
                        <PageWithTitle title={translate('navigation.profile')}>
                            <Navigate replace={true} to={userProfileUrl(myUserProfile.value.id)} />
                        </PageWithTitle>
                    }
                    path={profilePageRoute.patterns.users}
                />
                <Route
                    element={
                        <PageWithTitle title={translate('navigation.profile')}>
                            <Navigate replace={true} to={userProfileUrl(myUserProfile.value.id)} />
                        </PageWithTitle>
                    }
                    path={profilePageRoute.patterns.usersMe}
                />
                <Route
                    element={
                        <PageWithPageViewTracking>
                            <PageWithTitle title={translate('pages.global-search.search-headline')}>
                                <GlobalSearchPage />
                            </PageWithTitle>
                        </PageWithPageViewTracking>
                    }
                    path={globalSearchRoute.patterns.search}
                />
                <Route
                    element={
                        <PageWithTitle title={translate('navigation.dashboard')}>
                            <Navigate replace={true} to={dashboardPageRoute.patterns.dashboard} />
                        </PageWithTitle>
                    }
                    path="/login"
                />
                <Route
                    element={
                        <PageWithTitle title={translate('navigation.dashboard')}>
                            {isTrial ? (
                                initialPageForTrialTenant
                            ) : (
                                <Navigate replace={true} to={dashboardPageRoute.patterns.dashboard} />
                            )}
                        </PageWithTitle>
                    }
                    path="/"
                />
                <Route
                    element={
                        <PageWithTitle title={translate('common.home')}>
                            <Home />
                        </PageWithTitle>
                    }
                    path="/home"
                />
                <Route
                    element={
                        <PageWithTitle title={translate('common.welcome')}>
                            <Welcome />
                        </PageWithTitle>
                    }
                    path="/welcome"
                />
                <Route
                    element={
                        <PageWithTitle title={translate('common.onboarding')}>
                            <CompanyOnboardingSetupPage />
                        </PageWithTitle>
                    }
                    path={companyOnboardingRoutePath}
                />
                <Route
                    element={
                        <PageWithPageViewTracking>
                            <PageWithTitle title={translate('navigation.talent-directory')}>
                                <TalentDirectoryPage />
                            </PageWithTitle>
                        </PageWithPageViewTracking>
                    }
                    path={talentDirectoryPageRoute.patterns.talentDirectory}
                />
                <Route element={<Error404 />} path="*" />
            </Route>
        </Routes>
    );
};

type AppContentProperties = {
    readonly dependencies: AppDependencies;
    readonly hasNavigationMenu?: boolean;
    readonly isDataRoute: boolean;
    readonly layoutVariant?: LayoutVariant;
    readonly shouldShowFeedbackFloatingActionButton?: boolean;
};

const AppContent: FunctionComponent<AppContentProperties> = ({
    dependencies,
    hasNavigationMenu = true,
    isDataRoute,
    layoutVariant,
    shouldShowFeedbackFloatingActionButton = true,
}) => {
    const { authorizationProvider, translate } = dependencies;
    const fallbackDocumentTitle = translate('common.brand');
    const isMobile = false;
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        dependencies.featureFlagService.isInitialized().then((isInitialized) => {
            setIsInitialized(isInitialized);
        });

        // We fall back in case feature flag service is not initialized in time
        setTimeout(() => {
            setIsInitialized(true);
        }, 1000);
    }, [dependencies.featureFlagService]);

    if (authorizationProvider.isBlocked === true) {
        return (
            <PageWithTitle title={fallbackDocumentTitle}>
                <AccessDeniedPage />
            </PageWithTitle>
        );
    }

    if (isMobile) {
        return (
            <PageWithTitle title={fallbackDocumentTitle}>
                <AppBase
                    hasNavigationMenu={hasNavigationMenu}
                    isDataRoute={isDataRoute}
                    layoutVariant={layoutVariant}
                />
            </PageWithTitle>
        );
    }

    if (isInitialized === false) {
        return (
            <PageWithTitle title={fallbackDocumentTitle}>
                <div style={{ alignItems: 'center', display: 'flex', height: '100vh', justifyContent: 'center' }}>
                    <Spinner />
                </div>
            </PageWithTitle>
        );
    }

    if (authorizationProvider.isLoggedIn === true) {
        return (
            <PageWithTitle title={fallbackDocumentTitle}>
                <TerminologyInitializer />
                <SkillFeedbackModalRoot isWithFloatingActionButton={shouldShowFeedbackFloatingActionButton}>
                    <AppBase
                        hasNavigationMenu={hasNavigationMenu}
                        isDataRoute={isDataRoute}
                        layoutVariant={layoutVariant}
                        shouldShowFeedbackAction={shouldShowFeedbackFloatingActionButton}
                    />
                </SkillFeedbackModalRoot>
            </PageWithTitle>
        );
    }

    return (
        <PageWithTitle title={fallbackDocumentTitle}>
            <LandingPage />
        </PageWithTitle>
    );
};

type AppProperties = {
    readonly dependencies: AppDependencies;
    readonly environment: AppEnvironment;
    readonly queryClient: QueryClient;
};

type RootProperties = AppProperties & {
    readonly hasNavigationMenu?: boolean;
    readonly isDataRoute: boolean;
    readonly layoutVariant?: LayoutVariant;
    readonly pageTitle: string;
    readonly shouldShowFeedbackFloatingActionButton?: boolean;
};

const appCanvasStyles = css`
    html,
    body,
    #app {
        min-height: 100%;
        background-color: ${theme.colors.gray[200]};
    }

    #app {
        min-height: 100vh;
    }

    ::view-transition-group(*) {
        animation-duration: 400ms;
        animation-timing-function: cubic-bezier(0.455, 0.03, 0.515, 0.955);
    }
`;

const Root: FunctionComponent<RootProperties> = (properties) => {
    const {
        dependencies,
        environment,
        hasNavigationMenu,
        isDataRoute,
        layoutVariant,
        pageTitle,
        queryClient,
        shouldShowFeedbackFloatingActionButton,
    } = properties;

    return (
        <StrictMode>
            <QueryClientProvider client={queryClient}>
                <PageWithTitle title={pageTitle}>
                    <ErrorBoundary fallbackRender={renderErrorCodePage(dependencies.translate)}>
                        <AppWithContext dependencies={dependencies} environment={environment}>
                            <ApplicationFetchersProvider>
                                <MixpanelTrackingProvider client={mixpanel}>
                                    <CustomThemeProvider>
                                        <SnackbarProvider Components={snackbarComponents} maxSnack={3}>
                                            <CssBaseline />
                                            <Global styles={appCanvasStyles} />
                                            <TabProvider>
                                                <AppContent
                                                    dependencies={dependencies}
                                                    hasNavigationMenu={hasNavigationMenu}
                                                    isDataRoute={isDataRoute}
                                                    layoutVariant={layoutVariant}
                                                    shouldShowFeedbackFloatingActionButton={
                                                        shouldShowFeedbackFloatingActionButton
                                                    }
                                                />
                                            </TabProvider>
                                        </SnackbarProvider>
                                    </CustomThemeProvider>
                                </MixpanelTrackingProvider>
                            </ApplicationFetchersProvider>
                        </AppWithContext>
                    </ErrorBoundary>
                </PageWithTitle>
            </QueryClientProvider>
        </StrictMode>
    );
};

export const App: FunctionComponent<AppProperties> = (properties) => {
    const { dependencies, environment, queryClient } = properties;
    const { featureFlagService, translate, validatingCobrainerApiFetcher } = dependencies;

    const isDemoMenuEnabled = featureFlagService.isFeatureFlagEnabled('demo-menu');
    const dashboardV2Title = isDemoMenuEnabled
        ? translate('navigation.dashboard')
        : translate('navigation.dashboard-v2');
    const installationGuideTitle = translate('pages.installation-guide.title');

    const lazyInstallationGuidePage = async () => {
        const { InstallationGuidePage } = await import('../installation-guide');

        const GatedInstallationGuidePage: FunctionComponent = () => (
            <PageWithFeatureFlagEnabled enabledFeatureName="installation-guide" featureFlagService={featureFlagService}>
                <InstallationGuidePage />
            </PageWithFeatureFlagEnabled>
        );

        return { Component: GatedInstallationGuidePage };
    };

    const router = createBrowserRouter([
        {
            element: <RedirectToJobArchitectureV2 />,
            path: '/job-and-skill-architecture/*',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { JobArchitectureSelectPage } = await import(
                            '../job-architecture-v2/select-page/JobArchitectureSelectPage'
                        );

                        return {
                            Component: JobArchitectureSelectPage,
                        };
                    },
                },
                {
                    children: [
                        {
                            index: true,
                            lazy: async () => {
                                // prettier-ignore
                                const { OverviewTab } = await import('../job-architecture-v2/tabs/overview');

                                return {
                                    Component: OverviewTab,
                                };
                            },
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { OverviewTab } = await import('../job-architecture-v2/tabs/overview');

                                return {
                                    Component: OverviewTab,
                                };
                            },
                            path: 'overview',
                        },
                    ],
                    lazy: async () => {
                        // prettier-ignore
                        const { JobArchitectureSetupPage } = await import(
                            '../job-architecture-v2/JobArchitectureSetupPage'
                        );

                        return {
                            Component: JobArchitectureSetupPage,
                        };
                    },
                    path: ':id',
                },
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { GeneratingPage } = await import('../job-architecture-v2/setup-dialog/GeneratingPage');

                        return {
                            Component: GeneratingPage,
                        };
                    },
                    path: ':id/generating',
                },
                {
                    children: [
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { CompanyInformationPage } = await import(
                                    '../job-architecture-v2/setup-dialog/CompanyInformationPage'
                                );

                                return {
                                    Component: CompanyInformationPage,
                                };
                            },
                            path: 'company-information',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { OverviewPage } = await import(
                                    '../job-architecture-v2/setup-dialog/OverviewPage'
                                );

                                return {
                                    Component: OverviewPage,
                                };
                            },
                            path: 'overview',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { UploadDataPage } = await import(
                                    '../job-architecture-v2/setup-dialog/UploadDataPage'
                                );

                                return {
                                    Component: UploadDataPage,
                                };
                            },
                            path: 'upload-data',
                        },
                    ],
                    lazy: async () => {
                        // prettier-ignore
                        const { SetupDialogLayout } = await import(
                            '../job-architecture-v2/setup-dialog/SetupDialogLayout'
                        );

                        return {
                            Component: SetupDialogLayout,
                        };
                    },
                    path: ':id/setup-dialog',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.job-architecture')}
                    queryClient={queryClient}
                />
            ),
            path: '/job-architecture-v2',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { SkillsPage } = await import('../job-architecture-v2/skills');

                        return {
                            Component: SkillsPage,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.skills')}
                    queryClient={queryClient}
                />
            ),
            path: '/skills',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { LearningManagementPage } = await import('../learning-opportunities-v2');

                        return {
                            Component: LearningManagementPage,
                        };
                    },
                },
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { LearningManagementPage } = await import('../learning-opportunities-v2');

                        return {
                            Component: LearningManagementPage,
                        };
                    },
                    path: 'new',
                },
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { LearningManagementPage } = await import('../learning-opportunities-v2');

                        return {
                            Component: LearningManagementPage,
                        };
                    },
                    path: ':id',
                },
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { LearningManagementPage } = await import('../learning-opportunities-v2');

                        return {
                            Component: LearningManagementPage,
                        };
                    },
                    path: ':id/edit',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.learning-management')}
                    queryClient={queryClient}
                />
            ),
            path: '/learning-opportunities-v2',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { JobProfileStandaloneSetupPage } = await import(
                            '../job-architecture-v2/job-profile-standalone/JobProfileStandaloneSetupPage'
                        );

                        return {
                            Component: JobProfileStandaloneSetupPage,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.job-architecture')}
                    queryClient={queryClient}
                />
            ),
            path: '/job-profile/:architectureId/:itemType/:itemId',
        },
        {
            children: [
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { PositionManagement } = await import('../legacy/position-management/PositionManagement');

                        return {
                            Component: PositionManagement,
                        };
                    },
                    loader: createPositionManagementPageLoader(validatingCobrainerApiFetcher),
                    path: '*',
                },
                {
                    lazy: async () => {
                        const { PositionPage } = await import('../legacy/position/PositionPage');

                        return {
                            Component: PositionPage,
                        };
                    },
                    loader: createPositionsPageLoader(validatingCobrainerApiFetcher),
                    path: ':id/:mode?',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('navigation.position-management')}
                    queryClient={queryClient}
                />
            ),
            path: '/position-management/*',
        },
        {
            children: [
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { AlignmentRecordPage } = await import('../legacy/alignment-record/AlignmentRecordPage');

                        return {
                            Component: AlignmentRecordPage,
                        };
                    },
                    loader: createAlignmentRecordPageLoader(createFetchAlignmentRecord(validatingCobrainerApiFetcher)),
                    path: 'users/:userId/records/:id',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('navigation.alignment-record')}
                    queryClient={queryClient}
                    shouldShowFeedbackFloatingActionButton={false}
                />
            ),
            path: '/alignment-record/*',
        },
        {
            children: [
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { MyAlignmentPage } = await import('../legacy/my-alignment/MyAlignmentPage');

                        return {
                            Component: MyAlignmentPage,
                        };
                    },
                    loader: createMyAlignmentPageLoader(validatingCobrainerApiFetcher),
                    path: '*',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('navigation.my-alignment')}
                    queryClient={queryClient}
                    shouldShowFeedbackFloatingActionButton={false}
                />
            ),
            path: '/my-alignment/*',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { DebugPage } = await import('../debug/DebugPage');

                        return { Component: DebugPage };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('pages.debug.title')}
                    queryClient={queryClient}
                />
            ),
            path: debugRoute.patterns.debug,
        },
        {
            children: [
                {
                    children: [
                        {
                            index: true,
                            lazy: async () => {
                                // prettier-ignore
                                const { CompanySettingsIndexRedirect } = await import(
                                    '../company-settings/CompanySettingsIndexRedirect'
                                );

                                return { Component: CompanySettingsIndexRedirect };
                            },
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { OrganizationTab } = await import(
                                    '../company-settings/tabs/organization-tab/OrganizationTab'
                                );

                                return { Component: OrganizationTab };
                            },
                            path: 'organization',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { JobArchitectureTab } = await import(
                                    '../company-settings/tabs/job-architecture-tab/JobArchitectureTab'
                                );

                                return { Component: JobArchitectureTab };
                            },
                            path: 'job-architecture/:jobArchitectureId?',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { JobArchitectureTab } = await import(
                                    '../company-settings/tabs/job-architecture-tab/JobArchitectureTab'
                                );

                                return { Component: JobArchitectureTab };
                            },
                            path: 'job-architecture/:jobArchitectureId?/job-profile/:jobProfileView',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { PersonalPreferencesTab } = await import(
                                    '../company-settings/tabs/personal-preferences-tab/PersonalPreferencesTab'
                                );

                                return { Component: PersonalPreferencesTab };
                            },
                            path: 'personal-preferences',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { SubscriptionBillingTab } = await import(
                                    '../company-settings/tabs/subscription-billing-tab/SubscriptionBillingTab'
                                );

                                return { Component: SubscriptionBillingTab };
                            },
                            path: 'subscription-billing',
                        },
                    ],
                    lazy: async () => {
                        // prettier-ignore
                        const { CompanySettingsRoot } = await import('../company-settings/CompanySettingsRoot');

                        return { Component: CompanySettingsRoot };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.company-settings')}
                    queryClient={queryClient}
                />
            ),
            path: '/companySettings',
        },
        {
            children: [
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { StandaloneChatPocPageContent } = await import('../legacy/chat-poc/ChatPocPage');

                        return {
                            Component: StandaloneChatPocPageContent,
                        };
                    },
                    path: '*',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle="CHAT"
                    queryClient={queryClient}
                />
            ),
            path: '/chat-poc/*',
        },
        {
            children: [
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { OnboardingPage } = await import('./pages/onboarding/OnboardingNew');

                        return {
                            Component: OnboardingPage,
                        };
                    },
                    path: '*',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    hasNavigationMenu={false}
                    isDataRoute={true}
                    pageTitle={translate('pages.onboarding.headline')}
                    queryClient={queryClient}
                    shouldShowFeedbackFloatingActionButton={false}
                />
            ),
            path: '/onboarding/*',
        },
        {
            children: [
                {
                    children: [
                        {
                            index: true,
                            lazy: async () => {
                                // prettier-ignore
                                const { DocumentationLandingPage } = await import(
                                    './pages/documentation/DocumentationLandingPage'
                                );

                                return {
                                    Component: DocumentationLandingPage,
                                };
                            },
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { DocumentationArticlePage } = await import(
                                    './pages/documentation/pages/DocumentationArticlePage'
                                );

                                return {
                                    Component: DocumentationArticlePage,
                                };
                            },
                            path: ':slug',
                        },
                    ],
                    lazy: async () => {
                        // prettier-ignore
                        const { DocumentationLayout } = await import('./pages/documentation/DocumentationLayout');

                        return {
                            Component: DocumentationLayout,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('pages.documentation.title')}
                    queryClient={queryClient}
                />
            ),
            path: '/documentation',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { DashboardV2Page } = await import('./pages/dashboard-v2/DashboardV2');

                        return {
                            Component: DashboardV2Page,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={dashboardV2Title}
                    queryClient={queryClient}
                />
            ),
            path: '/dashboard-v2',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        const { SkillsChatGraph } = await import('../skill-chat');

                        return {
                            Component: SkillsChatGraph,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="canvas"
                    pageTitle="Home 2"
                    queryClient={queryClient}
                />
            ),
            path: '/home-2',
        },
        {
            children: [
                {
                    index: true,
                    lazy: lazyInstallationGuidePage,
                },
                {
                    lazy: lazyInstallationGuidePage,
                    path: ':jobArchitectureId',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={installationGuideTitle}
                    queryClient={queryClient}
                />
            ),
            path: '/installation-guide',
        },
        {
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={false}
                    pageTitle={translate('common.brand')}
                    queryClient={queryClient}
                />
            ),
            path: '*',
        },
    ]);

    return (
        <UrlTileQueryAdapter>
            <RouterProvider router={router} />
        </UrlTileQueryAdapter>
    );
};
